/* eslint-disable @typescript-eslint/no-explicit-any */
import { isAxiosError } from "axios";
import { logger, type LoggerArgs } from "core/logger";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@mindlercare/auth-ui";
import { RouterOutput, ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { useMindlerApi } from "api/apiv2";
import { useUserHasRole } from "./userHooks";

type BasePsychologist = RouterOutput["psychologists"]["findByUserId"];

type PsychologistWithoutDetails = Omit<BasePsychologist, "details">;

type PsychologistWithDetails = BasePsychologist & {
  details: NonNullable<BasePsychologist["details"]>;
};

export const usePsychologistUser = <T extends boolean = false>(opts?: {
  skip?: boolean;
  includeDetails?: T;
}) => {
  const { userId } = useAuth();
  const isUserPsychologist = useUserHasRole("Psychologist");
  const explicitlySkipped = opts?.skip === true;
  const shouldSkip = explicitlySkipped || !(userId && isUserPsychologist);

  const api = useMindlerApi();
  const { data: psychologist, isLoading } = useQuery(
    api.psychologists.findByUserId.queryOptions(
      {
        psychologistUserId: userId as number, // safe to cast since this hook is only used in a logged in state
        includeDetails: opts?.includeDetails ?? false,
      },
      {
        retryOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false, // important since 404 is returned if no psychologist is found
        refetchOnMount: false,
        enabled: !shouldSkip,
        staleTime: 300 * 1000, // 5 mins. This query considers the data stale for 5 mins unless invalidated -> almost like a state
      },
    ),
  );
  return {
    psychologist:
      (psychologist as T extends true
        ? PsychologistWithDetails
        : PsychologistWithoutDetails) ?? null,
    isLoading,
  };
};

export const useOnClickOutside = (
  ref: any,
  isOpen: boolean,
  handler: () => void,
  refsToIgnore?: any[],
) => {
  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      const isOutsideClick =
        isOpen && ref.current && !ref.current.contains(e.target);
      const isClickOnIgnoredRef = refsToIgnore?.some(
        (r) => r.current && r.current.contains(e.target),
      );
      if (isOutsideClick && !isClickOnIgnoredRef) {
        handler();
      }
    };

    document.addEventListener("mousedown", checkIfClickedOutside);

    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [isOpen, handler, ref, refsToIgnore]);
};

export const useHover = () => {
  const [value, setValue] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const handleMouseOver = useCallback(() => setValue(true), []);
  const handleMouseOut = useCallback(() => setValue(false), []);

  useEffect(() => {
    const node = ref.current;
    if (node) {
      node.addEventListener("mouseover", handleMouseOver);
      node.addEventListener("mouseout", handleMouseOut);

      return () => {
        node.removeEventListener("mouseover", handleMouseOver);
        node.removeEventListener("mouseout", handleMouseOut);
      };
    }
  }, [handleMouseOut, handleMouseOver]);

  return [ref, value];
};

export const useInterval = (callback: () => void, delay: number) => {
  const savedCallback = useRef<any>();

  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    function tick() {
      if (savedCallback?.current) {
        savedCallback.current();
      }
    }

    const id = setInterval(tick, delay);
    return () => clearInterval(id);
  }, [delay]);
};

/**
 * Pass in an object of named errors from rtk and logging details. This func handles
 * logging with metadata and returns combined error boolean status.
 */
export const useQueryErrorLogger = (
  possiblyErrors: Record<string, unknown>,
  ...[errorMessage, context]: LoggerArgs
) => {
  const { userId: currentUserId } = useAuth();

  const errors = Object.entries(possiblyErrors).filter(([_, error]) =>
    Boolean(error),
  );

  const dataLoadingError = errors.length > 0;

  useEffect(() => {
    errors.forEach(([errorName, error]) => {
      // Don't flood the logs with 401 and 403 errors as they're expected to happen
      // for some of our users
      const logLevel =
        typeof error === "object" &&
        error &&
        "errorCode" in error &&
        (error.errorCode === 401 || error.errorCode === 403)
          ? "warn"
          : "error";

      if (isAxiosError(error)) {
        // Axios error contains different sensitive data, so don't log the whole error
        logger[logLevel](errorMessage, {
          currentUserId,
          errorName,
          error: {
            code: error.code,
            status: error.status,
            name: error.name,
          },
          ...context,
        });
        return;
      }

      logger[logLevel](errorMessage, {
        currentUserId,
        error,
        errorName,
        ...context,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [possiblyErrors]);

  return dataLoadingError;
};

export const usePreviousValue = <T,>(value: T): T | undefined => {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  // eslint-disable-next-line react-hooks/refs
  return ref.current;
};

export * from "./eventhooks";
